import journeyIllustration from "assets/illustrations/journeyIllustration.svg"
import { useGraphqlQuery } from "graphql-mock"
import { useScrollToTopOnMount } from "hooks"
import { ErrorFallback, Loader } from "materia"
import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { HiddenHeader } from "../../components/HiddenHeader"
import { useJourneyNavigation } from "../../tools/useJourneyNavigation"
import { usePageTitle } from "../../tools/usePageTitle"
import { query, type Journey } from "./gql"
import { JourneyConnected } from "./Journey"
import { JourneyNext } from "./JourneyNext"

import s from "./styles.module.scss"

export const JourneyViewConnected = () => {
  const { journeyIdParam, navigateToJourney } = useJourneyNavigation()
  const { data, error, isPending } = useGraphqlQuery(query, {})

  // TODO [WEB-17784] I am adding a proper GraphQL field to get nextJourney
  // from the server. This workaround allows me to iterate before it's done
  // so I don't block myself. It will be gone in ~2 weeks
  const nextJourney = useMemo(() => {
    const journeys = data?.productCategories.nodes ?? []
    const currentJourneyIndex = journeys.findIndex(
      (journey) => journey.id === journeyIdParam
    )

    if (currentJourneyIndex === -1) {
      return undefined
    }

    return journeys[currentJourneyIndex + 1]
  }, [data, journeyIdParam])

  useEffect(() => {
    // Journey to show has already been decided - do nothing
    if (journeyIdParam) return

    // If no journey is selected to be shown yet, we default to the "nextJourney".
    // If there is no "nextJourney", we just show the first from the list.
    // If this also does not exist, then there is no journeys to show.
    const activeJourneyId = data?.nextJourney?.id
    const firstAvailableJourneyId = data?.productCategories.nodes[0]?.id
    const journeyToShowId = activeJourneyId ?? firstAvailableJourneyId

    if (journeyToShowId) navigateToJourney(journeyToShowId)
  }, [data, journeyIdParam, navigateToJourney])

  return (
    <JourneyView
      isLoading={isPending}
      error={error}
      journeyId={journeyIdParam}
      nextJourney={nextJourney}
    />
  )
}

type JourneyViewProps = {
  isLoading: boolean
  error: unknown
  journeyId: string | undefined
  nextJourney: Journey | undefined
}
export const JourneyView = ({
  isLoading,
  error,
  journeyId,
  nextJourney,
}: JourneyViewProps) => {
  const { t } = useTranslation()

  useScrollToTopOnMount()
  usePageTitle(t("pageTitle.JOURNEY"))

  if (isLoading) return <Loader />

  if (error)
    return (
      <ErrorFallback
        title={t("error.GENERIC")}
        message={t("error.JOURNEY_LIST_ERROR")}
        error={error}
      />
    )

  if (!journeyId) return <Empty />

  return (
    <div className={s.view}>
      <HiddenHeader title={t("navigation.JOURNEY")} tag="h1" />
      <JourneyConnected journeyId={journeyId} />
      {nextJourney && (
        <JourneyNext
          id={nextJourney.id}
          title={nextJourney.title}
          isLocked={nextJourney.isLocked}
        />
      )}
    </div>
  )
}

const Empty = () => {
  const { t } = useTranslation()

  return (
    <div className={s.empty}>
      <img src={journeyIllustration} className={s.empty__illustration} alt="" />
      <div className={s.empty__text}>{t("notFound.NOTFOUND_JOURNEY")}</div>
    </div>
  )
}
